/**
 * Consent record shape stored on the visitor's device.
 *
 * Written only once a choice has actually been made: an absent key means
 * undecided, never "denied but recorded". Storing the record itself is strictly
 * necessary (it is what keeps us from asking again), so it does not need consent.
 */
export interface ConsentCategories {
  /** Always true. Present so the stored record is self-describing. */
  readonly necessary: true;
  readonly analytics: boolean;
  /** Advertising measurement and targeting: the Meta Pixel and any ad tag in GTM. */
  readonly marketing: boolean;
}

/** The categories a visitor can actually choose. `necessary` is never a choice. */
export type ConsentChoice = Omit<ConsentCategories, 'necessary'>;

export interface ConsentRecord {
  readonly version: number;
  /** ISO timestamp of the choice, kept as the record of when consent was given. */
  readonly decidedAt: string;
  readonly categories: ConsentCategories;
}

export const CONSENT_STORAGE_KEY = 'aposmart_consent';

/**
 * Bump when the categories change meaning or a new category is added. A record
 * with any other version is treated as no consent at all and the banner asks
 * again, which is the only safe reading of a record we cannot interpret.
 *
 * 2: added `marketing`. A version-1 record never asked about marketing, so it
 * cannot be read as a marketing choice either way.
 *
 * The static widget guide (src/public/widget/index.html) reads this record with
 * its own copy of the key and version to gate its GTM container. Bump both, or
 * that page treats every record as undecided and loads nothing.
 */
export const CONSENT_VERSION = 2;

export function isConsentRecord(value: unknown): value is ConsentRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Partial<ConsentRecord>;
  return (
    record.version === CONSENT_VERSION &&
    typeof record.decidedAt === 'string' &&
    typeof record.categories === 'object' &&
    record.categories !== null &&
    typeof record.categories.analytics === 'boolean' &&
    typeof record.categories.marketing === 'boolean'
  );
}
